<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SlideOver from '@/components/shared/SlideOver.vue'
import { useOrganisationFeeOverride } from '@/composables/useOrganisationFeeOverride'
import type { OrganisationFeeOverride } from '@/types/pricing'

const props = defineProps<{
  open: boolean
  organisationId: number
  // The current RAW override (null = no override on that component today).
  currentPercentageBasisPoints: number | null
  currentFixedCents: number | null
}>()

const emit = defineEmits<{
  close: []
  updated: [override: OrganisationFeeOverride]
}>()

const { t } = useI18n()
const { override, actionLoading, actionError, fieldErrors, setOverride } = useOrganisationFeeOverride()

// "Use platform default" ticked => the component's value is sent as null.
// Zero is a deliberate, distinct override (spec §6.1.4) — hence explicit checkboxes.
const useDefaultPercentage = ref(true)
const percentageValue = ref(0)
const useDefaultFixed = ref(true)
const fixedValue = ref(0)
const reason = ref('')

const localErrors = ref<Record<string, string>>({})

const allErrors = computed<Record<string, string>>(() => {
  const merged: Record<string, string> = { ...localErrors.value }
  for (const [key, messages] of Object.entries(fieldErrors.value)) {
    if (messages.length > 0) merged[key] = messages[0]
  }
  return merged
})

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    useDefaultPercentage.value = props.currentPercentageBasisPoints === null
    percentageValue.value = props.currentPercentageBasisPoints ?? 0
    useDefaultFixed.value = props.currentFixedCents === null
    fixedValue.value = props.currentFixedCents ?? 0
    reason.value = ''
    localErrors.value = {}
    fieldErrors.value = {}
  },
)

function handleClose(): void {
  if (actionLoading.value) return
  emit('close')
}

function validate(): boolean {
  const errs: Record<string, string> = {}
  if (!useDefaultPercentage.value && (!Number.isInteger(percentageValue.value) || percentageValue.value < 0 || percentageValue.value > 10000)) {
    errs.processing_fee_percentage_basis_points = t('admin.organisations.fee_error_percentage_range')
  }
  if (!useDefaultFixed.value && (!Number.isInteger(fixedValue.value) || fixedValue.value < 0 || fixedValue.value > 100000)) {
    errs.processing_fee_fixed_cents = t('admin.organisations.fee_error_fixed_range')
  }
  if (reason.value.trim().length < 10 || reason.value.length > 500) {
    errs.reason = t('admin.organisations.fee_error_reason_length')
  }
  localErrors.value = errs
  return Object.keys(errs).length === 0
}

async function handleSubmit(): Promise<void> {
  if (!validate()) return

  const success = await setOverride(props.organisationId, {
    processing_fee_percentage_basis_points: useDefaultPercentage.value ? null : percentageValue.value,
    processing_fee_fixed_cents: useDefaultFixed.value ? null : fixedValue.value,
    reason: reason.value.trim(),
  })

  if (success && override.value) {
    emit('updated', override.value)
    emit('close')
  }
}
</script>

<template>
  <SlideOver
    :open="open"
    :title="t('admin.organisations.fee_edit')"
    :saving="actionLoading"
    @close="handleClose"
  >
    <div
      v-if="actionError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
      role="alert"
    >
      {{ t(actionError) }}
    </div>

    <div>
      <label class="flex items-center gap-2 text-sm text-slate-300">
        <input v-model="useDefaultPercentage" type="checkbox" class="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500" />
        {{ t('admin.organisations.fee_use_default') }} &mdash; {{ t('admin.pricing.field_fee_percentage') }}
      </label>
      <input
        v-if="!useDefaultPercentage"
        v-model.number="percentageValue"
        type="number"
        min="0"
        max="10000"
        class="mt-2 w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': allErrors.processing_fee_percentage_basis_points }"
      />
      <p v-if="!useDefaultPercentage" class="mt-1 text-xs text-slate-500">
        = {{ (percentageValue / 100).toFixed(2) }}%
      </p>
      <p v-if="allErrors.processing_fee_percentage_basis_points" class="mt-1 text-xs text-red-400">
        {{ allErrors.processing_fee_percentage_basis_points }}
      </p>
    </div>

    <div>
      <label class="flex items-center gap-2 text-sm text-slate-300">
        <input v-model="useDefaultFixed" type="checkbox" class="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500" />
        {{ t('admin.organisations.fee_use_default') }} &mdash; {{ t('admin.pricing.field_fee_fixed') }}
      </label>
      <input
        v-if="!useDefaultFixed"
        v-model.number="fixedValue"
        type="number"
        min="0"
        max="100000"
        class="mt-2 w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': allErrors.processing_fee_fixed_cents }"
      />
      <p v-if="!useDefaultFixed" class="mt-1 text-xs text-slate-500">
        = EUR {{ (fixedValue / 100).toFixed(2) }}
      </p>
      <p v-if="allErrors.processing_fee_fixed_cents" class="mt-1 text-xs text-red-400">
        {{ allErrors.processing_fee_fixed_cents }}
      </p>
    </div>

    <div>
      <label for="fee-override-reason" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('admin.pricing.reason_label') }}
      </label>
      <textarea
        id="fee-override-reason"
        v-model="reason"
        rows="3"
        maxlength="500"
        class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': allErrors.reason }"
      />
      <div class="mt-1 flex items-center justify-between">
        <p v-if="allErrors.reason" class="text-xs text-red-400">{{ allErrors.reason }}</p>
        <p v-else class="text-xs text-slate-500">{{ t('admin.pricing.reason_help') }}</p>
        <p class="text-xs text-slate-500 shrink-0 ml-2">{{ reason.length }}/500</p>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        :disabled="actionLoading"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
        @click="handleClose"
      >
        {{ t('admin.common.cancel') }}
      </button>
      <button
        type="button"
        :disabled="actionLoading"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="handleSubmit"
      >
        {{ t('admin.common.save') }}
      </button>
    </template>
  </SlideOver>
</template>
